// C:\Projects\texe\texe-web\src\pages\NewPost.tsx
// NewPost with Ads wrapper will be added at the end of file
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiGet, apiPost, API_BASE_URL } from "../lib/api";
import { searchUsers, type UserSearchItem } from "../lib/userSearchApi";
import Avatar from "../components/Avatar";
import PageWithAds from "../components/PageWithAds";

type LimitRes = {
  ok: true;
  canPost: boolean;
  nextAllowedAt: string | null;
  secondsLeft: number;
};

function abs(url?: string | null) {
  if (!url) return undefined;
  return url.startsWith("/") ? `${API_BASE_URL}${url}` : url;
}

function NewPost() {
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [info, setInfo] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // 24 saatlik kuralın durumu
  const [limit, setLimit] = useState<LimitRes | null>(null);

  // mention UI state
  const taRef = useRef<HTMLTextAreaElement | null>(null);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQ, setMentionQ] = useState("");
  const [mentionItems, setMentionItems] = useState<UserSearchItem[]>([]);
  const [mentionIdx, setMentionIdx] = useState(-1);
  const [mentionPos, setMentionPos] = useState<{ top: number; left: number } | null>(null);

  // İlk yüklemede limit durumunu çek
  useEffect(() => {
    refreshLimit().catch(() => {});
  }, []);

  // Her saniye geri sayım
  useEffect(() => {
    if (!limit || limit.secondsLeft <= 0) return;
    const t = setInterval(() => {
      setLimit((s) => (s ? { ...s, secondsLeft: Math.max(0, s.secondsLeft - 1) } : s));
    }, 1000);
    return () => clearInterval(t);
  }, [limit?.secondsLeft]);

  async function refreshLimit() {
    try {
      const r = await apiGet<LimitRes>("/api/limits/post");
      setLimit(r);
    } catch {
      // sessiz geç
    }
  }

  async function submit() {
    setLoading(true);
    setInfo(null);
    setErr(null);
    try {
      if (limit && !limit.canPost) {
        setErr("24 saatlik limit nedeniyle şu an paylaşamazsın.");
        return;
      }
      const res = await apiPost<{ ok: true; id: string; createdAt: string }>("/api/posts", { text });
      setInfo(`Gönderildi!`);
      setText("");
      await refreshLimit();
      
      // 1.5 saniye sonra ana sayfaya yönlendir
      setTimeout(() => {
        navigate("/feed");
      }, 1500);
    } catch (e: any) {
      const msg = String(e?.message || "");
      if (msg === "limit_24h") {
        await refreshLimit();
        setErr("Limit: 24 saatte yalnızca 1 paylaşım yapabilirsin. Kalan süre dolunca tekrar dene.");
      } else {
        setErr("Gönderilemedi.");
      }
    } finally {
      setLoading(false);
    }
  }

  const left = 1000 - text.length;
  const disabled = loading || text.trim().length === 0 || (limit ? !limit.canPost : false);

  const cooldownLabel = useMemo(() => {
    if (!limit) return null;
    if (limit.canPost || limit.secondsLeft <= 0) return "Hemen paylaşabilirsin";
    const s = limit.secondsLeft;
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const ss = s % 60;
    return `${h} saat ${m} dakika ${ss} saniye kaldı`;
  }, [limit?.secondsLeft, limit?.canPost]);

  // ──────────────────────────────
  // Mention logic
  // ──────────────────────────────
  function getCaretClientPos() {
    const ta = taRef.current;
    if (!ta) return null;
    const { selectionStart } = ta;
    const rect = ta.getBoundingClientRect();
    const lineHeight = 24;
    const lines = (ta.value.slice(0, selectionStart).match(/\n/g)?.length ?? 0);
    return { top: rect.top + window.scrollY + 8 + lines * lineHeight, left: rect.left + window.scrollX + 12 };
  }

  function maybeOpenMention(e?: React.KeyboardEvent<HTMLTextAreaElement>) {
    const ta = taRef.current;
    if (!ta) return;
    const { selectionStart } = ta;
    const before = ta.value.slice(0, selectionStart);
    const match = /(^|[\s.,;:!?()[\]{}"'])@([A-Za-z0-9_.]{0,20})$/.exec(before);
    if (match) {
      const q = match[2] || "";
      setMentionQ(q);
      setMentionIdx(-1);
      setMentionOpen(true);
      const pos = getCaretClientPos();
      setMentionPos(pos);
      debouncedFetch(q);
    } else {
      setMentionOpen(false);
      setMentionQ("");
      setMentionItems([]);
      setMentionIdx(-1);
    }
  }

  const fetchTimer = useRef<number | null>(null);
  function debouncedFetch(q: string) {
    if (fetchTimer.current) window.clearTimeout(fetchTimer.current);
    fetchTimer.current = window.setTimeout(async () => {
      const items = q ? await searchUsers(q, 5) : [];
      setMentionItems(items);
    }, 200);
  }

  function insertUsername(username: string) {
    const ta = taRef.current;
    if (!ta) return;
    const { selectionStart, selectionEnd } = ta;
    const before = text.slice(0, selectionStart);
    const after = text.slice(selectionEnd);
    const startMatch = /(^|[\s.,;:!?()[\]{}"'])(@([A-Za-z0-9_.]{0,20}))$/.exec(before);
    const startIdx = startMatch ? selectionStart - (startMatch[2]?.length ?? 0) : selectionStart;
    const prefix = text.slice(0, startIdx);
    const newVal = `${prefix}@${username} ${after}`;
    const newCaret = (prefix + "@" + username + " ").length;

    setText(newVal);
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(newCaret, newCaret);
    });

    setMentionOpen(false);
    setMentionQ("");
    setMentionItems([]);
    setMentionIdx(-1);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (mentionOpen) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setMentionIdx((v) => Math.min(mentionItems.length - 1, v + 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setMentionIdx((v) => Math.max(-1, v - 1));
        return;
      }
      if (e.key === "Enter") {
        if (mentionIdx >= 0 && mentionItems[mentionIdx]) {
          e.preventDefault();
          insertUsername(mentionItems[mentionIdx].username);
          return;
        }
      }
      if (e.key === "Escape") {
        setMentionOpen(false);
        return;
      }
    }
  }

  useEffect(() => {
    if (!mentionOpen) return;
    const onScrollOrResize = () => setMentionPos(getCaretClientPos());
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [mentionOpen]);

  return (
    <div style={{ 
      minHeight: "100vh",
      backgroundColor: "#000",
      paddingTop: 80,
      paddingBottom: 40,
    }}>
      <div style={{ 
        maxWidth: 680, 
        margin: "0 auto",
        padding: "0 24px",
      }}>
        {/* Logo + Title */}
        <div style={{
          textAlign: "center",
          marginBottom: 32,
        }}>
          <img 
            src="/logo/logo.png" 
            alt="SavNot Logo" 
            style={{ 
              width: 120, 
              height: 120,
              marginBottom: 16,
            }} 
          />
          <h1 style={{
            color: "#114232",
            fontSize: 28,
            fontWeight: 800,
            marginBottom: 8,
          }}>
            Yeni Paylaşım
          </h1>
          <p style={{
            color: "#888",
            fontSize: 14,
          }}>
            Düşüncelerini özgürce paylaş, toplulukla etkileşime geç.
          </p>
        </div>

        {/* 24h Geri Sayım */}
        {limit && !limit.canPost && (
          <div style={{
            backgroundColor: "#1a1a1a",
            border: "2px solid #114232",
            borderRadius: 12,
            padding: 16,
            marginBottom: 24,
            display: "flex",
            alignItems: "center",
            gap: 16,
          }}>
            <div style={{
              fontSize: 32,
            }}>
              ⏰
            </div>
            <div>
              <div style={{
                color: "#114232",
                fontSize: 14,
                fontWeight: 700,
                marginBottom: 4,
              }}>
                Sonraki Paylaşım
              </div>
              <div style={{
                color: "#FFD700",
                fontSize: 18,
                fontWeight: 600,
              }}>
                {cooldownLabel}
              </div>
            </div>
          </div>
        )}

        {/* Input Container */}
        <div style={{ position: "relative", marginBottom: 16 }}>
          <textarea
            ref={taRef}
            value={text}
            onChange={(e) => {
              setText(e.target.value.slice(0, 1000));
              setTimeout(() => maybeOpenMention(), 0);
            }}
            onKeyDown={onKeyDown}
            onKeyUp={() => maybeOpenMention()}
            rows={10}
            style={{
              width: "100%",
              padding: 16,
              borderRadius: 12,
              border: "2px solid #114232",
              backgroundColor: "#1a1a1a",
              color: "#fff",
              fontSize: 15,
              lineHeight: "24px",
              resize: "vertical",
              fontFamily: "inherit",
            }}
            placeholder="Bugünkü düşünceni yaz... (@kullaniciadi ile bahset)"
            disabled={loading || (limit ? !limit.canPost : false)}
            autoFocus
          />

          {/* Mention dropdown */}
          {mentionOpen && mentionPos && (
            <div
              role="listbox"
              style={{
                position: "absolute",
                top: mentionPos.top - (taRef.current?.getBoundingClientRect().top ?? 0) + 32,
                left: 16,
                background: "#0b0b0b",
                border: "2px solid #114232",
                borderRadius: 12,
                padding: 8,
                boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
                maxHeight: 280,
                overflow: "auto",
                zIndex: 30,
                minWidth: 280,
              }}
            >
              {mentionItems.length === 0 ? (
                <div style={{ 
                  padding: 12, 
                  color: "#888",
                  textAlign: "center",
                }}>
                  {mentionQ.length === 0 
                    ? "Kullanıcı adı yazmaya başla..." 
                    : "Kullanıcı bulunamadı"}
                </div>
              ) : (
                <>
                  <div style={{
                    padding: "8px 12px",
                    borderBottom: "1px solid #222",
                    marginBottom: 4,
                  }}>
                    <div style={{
                      color: "#114232",
                      fontSize: 12,
                      fontWeight: 700,
                    }}>
                      Takip ettiklerin
                    </div>
                  </div>
                  {mentionItems.map((u, i) => (
                    <div
                      key={u.id}
                      role="option"
                      aria-selected={i === mentionIdx}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        insertUsername(u.username);
                      }}
                      onMouseEnter={() => setMentionIdx(i)}
                      style={{
                        display: "flex",
                        gap: 12,
                        alignItems: "center",
                        padding: 10,
                        borderRadius: 8,
                        background: i === mentionIdx ? "#1a1a1a" : "transparent",
                        cursor: "pointer",
                      }}
                    >
                      <Avatar src={abs(u.avatarUrlSmall || u.avatarUrl)} alt={u.username} size={36} />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{
                          fontWeight: 600,
                          color: "#fff",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}>
                          @{u.username}
                        </div>
                        {u.bio && (
                          <div style={{
                            color: "#888",
                            fontSize: 12,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}>
                            {u.bio}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}>
          <span style={{ color: "#888", fontSize: 14 }}>
            {left} karakter kaldı
          </span>
          <button
            disabled={disabled}
            onClick={submit}
            style={{
              padding: "12px 32px",
              borderRadius: 8,
              border: "none",
              background: disabled ? "#333" : "#114232",
              color: disabled ? "#666" : "#fff",
              fontSize: 16,
              fontWeight: 600,
              cursor: disabled ? "not-allowed" : "pointer",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              if (!disabled) e.currentTarget.style.transform = "scale(1.02)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
            }}
          >
            {loading ? "Gönderiliyor..." : limit?.canPost === false ? "Bekle" : "Paylaş"}
          </button>
        </div>

        {info && (
          <div style={{
            padding: 12,
            borderRadius: 8,
            backgroundColor: "rgba(17, 66, 50, 0.2)",
            border: "1px solid #114232",
            color: "#4CAF50",
            textAlign: "center",
          }}>
            {info}
          </div>
        )}
        {err && (
          <div style={{
            padding: 12,
            borderRadius: 8,
            backgroundColor: "rgba(211, 47, 47, 0.1)",
            border: "1px solid #D32F2F",
            color: "#EF5350",
            textAlign: "center",
          }}>
            {err}
          </div>
        )}
      </div>
    </div>
  );
}

export default function NewPostWithAds() {
  return (
    <PageWithAds leftAdSlot="6766671940" rightAdSlot="6324118831">
      <NewPost />
    </PageWithAds>
  );
}
